import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { match } from 'ts-pattern'

const useCanteenMenuFiltersStateUserDataFragment = graphql(`
  fragment UseCanteenMenuFiltersState_UserDataFragment on User {
    allergens {
      celery
      cereals
      crustaceans
      eggs
      fish
      lupin
      milk
      molluscs
      mustard
      nuts
      peanuts
      sesame
      soybeans
      sulphur
    }
    dietaryPreferences {
      vegan
      vegetarian
      halal
      kosher
      refinedSugar
    }
  }
`)

type Props = {
  userData?: FragmentType<typeof useCanteenMenuFiltersStateUserDataFragment> | null
  paramsVendors: string
  paramsDietaries: string
  paramsAllergens: string
}

export const useCcMenuFiltersState = ({
  userData: userDataProp,
  paramsAllergens,
  paramsDietaries,
  paramsVendors,
}: Props) => {
  const userData = useFragment(useCanteenMenuFiltersStateUserDataFragment, userDataProp)
  const [dietaries, setDietaries] = useState<string[]>([])
  const [allergens, setAllergens] = useState<string[]>([])
  const [vendors, setVendors] = useState<string[]>([])

  const setMultipleValues = useCallback(
    (category: 'vendors' | 'allergens' | 'dietaries', values: string[]) => {
      match(category)
        .with('dietaries', () => {
          setDietaries((prev) => Array.from(new Set([...prev, ...values])))
        })
        .with('allergens', () => {
          setAllergens((prev) => Array.from(new Set([...prev, ...values])))
        })
        .with('vendors', () => {
          setVendors((prev) => Array.from(new Set([...prev, ...values])))
        })
    },
    [],
  )

  useEffect(() => {
    const allergensFromParams = paramsAllergens.length > 0 ? paramsAllergens.split(',') : []
    const dietariesFromParams = paramsDietaries.length > 0 ? paramsDietaries.split(',') : []
    const vendorsFromParams = paramsVendors.length > 0 ? paramsVendors.split(',') : []
    setMultipleValues('allergens', allergensFromParams)
    setMultipleValues('vendors', vendorsFromParams)
    setMultipleValues('dietaries', dietariesFromParams)
  }, [paramsAllergens, paramsDietaries, paramsVendors, setMultipleValues])

  useEffect(() => {
    if (!userData) return
    const userAllergens = { ...userData.allergens }
    delete userAllergens.__typename
    const userDietaryPreferences = { ...userData.dietaryPreferences }
    delete userDietaryPreferences.__typename

    const allergensToSet = Object.entries(userAllergens)
      .filter(([, value]) => value)
      .map(([key]) => key)

    const dietariesToSet = Object.entries(userDietaryPreferences)
      .filter(([, value]) => value)
      .map(([key]) => key)

    if (allergensToSet.includes('nuts')) {
      allergensToSet.push('peanuts')
    }

    if (allergensToSet.length) setMultipleValues('allergens', allergensToSet)
    if (dietariesToSet.length) setMultipleValues('dietaries', dietariesToSet)
  }, [userData, setMultipleValues])

  const handleToggle = (value: string, setValue: Dispatch<SetStateAction<string[]>>) => {
    setValue((prev) => {
      const newValue = [...prev]
      const indexOfValue = newValue.indexOf(value)
      indexOfValue === -1 ? newValue.push(value) : newValue.splice(indexOfValue, 1)
      return newValue
    })
  }

  const toggleFilterValue = (category: 'vendors' | 'allergens' | 'dietaries', value: string) => {
    match(category)
      .with('allergens', () => handleToggle(value, setAllergens))
      .with('dietaries', () => handleToggle(value, setDietaries))
      .with('vendors', () => handleToggle(value, setVendors))
      .exhaustive()
  }

  const clearFilters = () => {
    setDietaries([])
    setAllergens([])
    setVendors([])
  }

  return { filters: { dietaries, allergens, vendors }, toggleFilterValue, clearFilters }
}
